import { defineStore } from 'pinia'
import type { QueueEntry, CampaignField, NumberListField } from '@/shared/api/queue'
import type { FormTemplate } from '@/shared/api/forms'
import { useToasts } from './toasts'

export type CallStatus = 'idle' | 'dialing' | 'ringing' | 'in_call' | 'on_hold' | 'ended'

export const useActiveCallStore = defineStore('active_call', {
  state: () => ({
    selectedEntry: null as QueueEntry | null,
    dialNumber: '',
    callStatus: 'idle' as CallStatus,
    callStartedAt: null as string | null,
    callAnsweredAt: null as string | null,
    callEndedAt: null as string | null,
    muted: false,
    onHold: false,
    showDisposition: false,
    completedEntry: null as QueueEntry | null,
    autoCallToken: 0,
    // Note prise pendant l'appel (partagée avec la fiche de fin d'appel).
    callNote: '',
    // RDV planifié pendant l'appel quand la campagne n'a PAS de formulaire
    // RDV rattaché (simple mémo date/heure de l'espace de travail).
    plannedAppointmentAt: null as string | null,

    // ── Fiche partagée (espace de travail ↔ fin d'appel) ─────────────────
    // Chargée par l'InCallWorkspace, lue/validée par la fiche de fin
    // d'appel : une seule saisie, éditable pendant ET après l'appel.
    // `ficheEntryId` garantit que ces données appartiennent bien à l'entrée
    // en cours de clôture (un 2e appel peut recharger la fiche entre-temps).
    ficheEntryId: null as number | null,
    customFields: [] as CampaignField[],
    // Champs perso définis par la LISTE d'appartenance du numéro (mêmes valeurs
    // que customFields : partagées dans custom_data via `fieldValues`).
    listFields: [] as NumberListField[],
    fieldValues: {} as Record<string, any>,
    apptForm: null as FormTemplate | null,
    apptObtained: false,
    apptValues: {} as Record<string, any>,
    // Partenaire PexiLine choisi pour le RDV : '' = pas encore choisi,
    // 'none' = sans partenaire, sinon l'id PexiLine du partenaire.
    pexilinePartnerId: '' as string,
    // Posé par l'InCallWorkspace : sauvegarde silencieuse de la fiche,
    // déclenchée par la fin d'appel avant d'enregistrer la disposition
    // (les édits faits après le raccroché ne sont jamais perdus).
    flushFiche: null as (() => Promise<void>) | null,
  }),
  getters: {
    isActive: (s) => s.callStatus !== 'idle' && s.callStatus !== 'ended',
    // Occupé = appel en cours OU fiche de fin d'appel encore ouverte
    // (post-qualif). Tant que c'est vrai, on refuse de composer un nouvel appel.
    isBusy: (s) =>
      (s.callStatus !== 'idle' && s.callStatus !== 'ended') || s.showDisposition,
    canDial: (s) =>
      !!s.dialNumber
      && (s.callStatus === 'idle' || s.callStatus === 'ended')
      && !s.showDisposition,
  },
  actions: {
    selectFromQueue(entry: QueueEntry) {
      this.selectedEntry = entry
      this.dialNumber = entry.phone
    },
    setDial(num: string) {
      this.dialNumber = num
      if (!this.selectedEntry || this.selectedEntry.phone !== num) {
        this.selectedEntry = null
      }
    },
    /**
     * Lance un appel vers `num` depuis un point d'entrée externe (Historique,
     * Contacts, Tickets, Rappels). Refuse de composer tant qu'un appel est en
     * cours ou qu'une fiche de fin d'appel n'est pas clôturée : sinon
     * l'INVITE SIP partirait sans UI visible (le DispositionModal occupe le
     * softphone). Renvoie `false` si l'appel a été bloqué.
     */
    dialAndCall(num: string): boolean {
      if (this.isBusy) {
        useToasts().warning(
          'Appel en cours',
          'Terminez et clôturez l\'appel en cours avant d\'en passer un autre.',
        )
        return false
      }
      this.dialNumber = num
      this.autoCallToken++
      return true
    },
    startCall() {
      this.callStatus = 'dialing'
      this.callStartedAt = new Date().toISOString()
      this.callAnsweredAt = null
      this.callEndedAt = null
    },
    answered() {
      this.callStatus = 'in_call'
      this.callAnsweredAt = new Date().toISOString()
    },
    endCall() {
      this.callStatus = 'ended'
      this.callEndedAt = new Date().toISOString()
      if (this.selectedEntry) {
        this.completedEntry = this.selectedEntry
        this.showDisposition = true
      }
    },
    reset() {
      this.selectedEntry = null
      this.dialNumber = ''
      this.callStatus = 'idle'
      this.callStartedAt = null
      this.callAnsweredAt = null
      this.callEndedAt = null
      this.muted = false
      this.onHold = false
      this.callNote = ''
      this.plannedAppointmentAt = null
      this.ficheEntryId = null
      this.customFields = []
      this.listFields = []
      this.fieldValues = {}
      this.apptForm = null
      this.apptObtained = false
      this.apptValues = {}
      this.pexilinePartnerId = ''
    },
    closeDisposition() {
      this.showDisposition = false
      this.completedEntry = null
      this.reset()
    },
  },
})
